import Vue from 'vue'

/*
 * En GET, les paramètres de requête passent par `data`, pas par `params` : auth.fetch recompose
 * sa configuration axios à la main et ne recopie pas `params` (voir ExportRepository).
 */

/**
 * Les commandes d'équipement (contexte Commissioning) : une borne, un compteur, un véhicule, des
 * badges, pour un membre ou pour l'organisation elle-même — de la commande à la mise en service.
 */
class CommissioningRepository {

  // GET /api/commissioning/orders — { total, items }
  // step : les étapes de la file de travail (la plus ancienne à l'étape d'abord)
  list({ organizationId, kind, holder, status, step, technicianId, search, offset, limit } = {}) {
    const params = {}
    if (step && step.length) params.step = step
    if (organizationId) params.organizationId = organizationId
    if (kind) params.kind = kind
    if (holder) params.holder = holder
    if (status && status.length) params.status = status
    if (technicianId) params.technicianId = technicianId
    if (search) params.search = search
    if (offset !== undefined) params.offset = offset
    if (limit !== undefined) params.limit = limit
    return this.fetch({ url: '/api/commissioning/orders', method: 'get', data: params })
  }

  // GET /api/commissioning/order-steps — { steps: { approval: 3, shipment: 12, … } } : combien attendent à chaque étape
  steps({ organizationId, kind, holder, search } = {}) {
    const params = {}
    if (organizationId) params.organizationId = organizationId
    if (kind) params.kind = kind
    if (holder) params.holder = holder
    if (search) params.search = search
    return this.fetch({ url: '/api/commissioning/order-steps', method: 'get', data: params })
  }

  // PUT /api/commissioning/orders/{id}/badges/{kind} — { identifier, confirmed? } → { order, verdict } : le badge saisi est
  // vérifié et enregistré (réservé, pas encore rattaché)
  holdBadge(id, kind, identifier, confirmed = false) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/badges/${kind}`, method: 'put', data: { identifier, confirmed } })
  }

  // DELETE /api/commissioning/orders/{id}/badges/{kind} → { order } : la saisie de ce badge est retirée
  releaseBadge(id, kind) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/badges/${kind}`, method: 'delete' })
  }

  // GET /api/commissioning/orders/{id} — la commande, et son plan de mise en service s'il a été lancé
  get(id) {
    return this.fetch({ url: `/api/commissioning/orders/${id}`, method: 'get' })
  }

  // POST /api/commissioning/orders — le demandeur est l'utilisateur connecté ; rend la commande créée
  create(order) {
    return this.fetch({ url: '/api/commissioning/orders', method: 'post', data: order })
  }

  // POST /api/commissioning/orders/{id}/decision — decision : approve | reject (reason exigé)
  decide(id, decision, reason = '') {
    return this.fetch({ url: `/api/commissioning/orders/${id}/decision`, method: 'post', data: { decision, reason } })
  }

  // GET /api/commissioning/technicians — ceux à qui le lecteur peut confier une pose
  technicians() {
    return this.fetch({ url: '/api/commissioning/technicians', method: 'get' })
  }

  // POST /api/commissioning/orders/{id}/assignment — technicianId null pour libérer
  assign(id, technicianId) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/assignment`, method: 'post', data: { technicianId } })
  }

  // POST /api/commissioning/orders/{id}/survey — mode video | on-site, ou null (plus de visite) ; scheduledAt ISO 8601 ou null
  planSurvey(id, mode, scheduledAt) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/survey`, method: 'post', data: { mode, scheduledAt } })
  }

  // POST /api/commissioning/orders/{id}/survey/outcome — pas faisable : la raison est exigée, la commande se clôt
  recordSurvey(id, feasible, notes) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/survey/outcome`, method: 'post', data: { feasible, notes } })
  }

  // POST /api/commissioning/orders/{id}/schedule — scheduledAt ISO 8601, ou null
  schedule(id, scheduledAt) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/schedule`, method: 'post', data: { scheduledAt } })
  }

  // POST /api/commissioning/orders/{id}/cancellation
  cancel(id, reason) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/cancellation`, method: 'post', data: { reason } })
  }

  // POST /api/commissioning/orders/{id}/commissioning-retry — relance la mise en service d'une commande sans pose
  // (véhicule, badges) ; rend la commande et l'issue (`retry` : launched, already-active, failed)
  retryCommissioning(id) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/commissioning-retry`, method: 'post' })
  }

  // POST /api/commissioning/orders/{id}/reminder — relance la personne (étape « person », une fois par jour) ; rend la commande
  remind(id) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/reminder`, method: 'post' })
  }

  // PATCH /api/commissioning/orders/{id}/site — { siteContact: { name, phone }, installationNotes } : ce que le technicien lira
  changeSite(id, siteContact, installationNotes) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/site`, method: 'patch', data: { siteContact, installationNotes } })
  }

  // POST /api/commissioning/orders/{id}/badge-check — { identifier } → { status, needsConfirmation, badge, uid,
  // printedNumber, state, organizationName, customerName, sameOrganization } (status : free, other-organization,
  // out-of-stock, unusable, unknown, out-of-perimeter, reserved)
  checkBadge(id, identifier) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/badge-check`, method: 'post', data: { identifier } })
  }

  // POST /api/commissioning/orders/{id}/shipment — { professional?, personal?, confirmed? } : l'envoi est validé
  // (courrier imprimé, badges sortis du stock sans être activés)
  shipBadgeOrder(id, badges) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/shipment`, method: 'post', data: badges })
  }

  // POST /api/commissioning/orders/{id}/badge-validations/{uid} — { accept, reason? } → la commande : ZEborne active le
  // badge présenté hors stock pour la personne, ou le refuse en disant pourquoi (opérateur seul)
  decideBadgeValidation(id, uid, decision) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/badge-validations/${uid}`, method: 'post', data: decision })
  }

  // POST /api/commissioning/orders/{id}/badge-handover — { confirmed? } : les badges enregistrés sortent du stock, sans
  // être activés (la personne les active)
  handOverBadges(id, badges) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/badge-handover`, method: 'post', data: badges })
  }

  // POST /api/commissioning/orders/{id}/badge-letter/preview — { professional?, personal? } → URL (blob) de l'aperçu PDF
  previewBadgeLetter(id, badges) {
    return this.pdf({ url: `/api/commissioning/orders/${id}/badge-letter/preview`, method: 'post', data: badges })
  }

  // GET /api/commissioning/orders/{id}/badge-letter/file → URL (blob) du courrier imprimé
  badgeLetterFile(id) {
    return this.pdf({ url: `/api/commissioning/orders/${id}/badge-letter/file`, method: 'get' })
  }

  // PUT /api/commissioning/orders/{id}/shipping-address — { address } : la logistique corrige l'adresse d'envoi
  changeShippingAddress(id, address) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/shipping-address`, method: 'put', data: { address } })
  }

  /** Un PDF rendu par l'API, prêt à afficher dans un cadre ou un nouvel onglet. */
  pdf(options) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ ...options, responseType: 'arraybuffer' })
        .then(response => resolve(window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))))
        .catch(err => {
          // Le refus arrive en JSON dans un tampon : on le relit pour que l'écran puisse dire pourquoi.
          try {
            err.response.data = JSON.parse(new TextDecoder().decode(err.response.data))
          } catch (ignored) { /* pas du JSON */ }
          reject(err)
        })
    })
  }

  // GET /api/commissioning/orders/{id}/badge-letter — { url } du courrier, lien temporaire
  badgeLetter(id) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/badge-letter`, method: 'get' })
  }

  // GET /api/commissioning/orders/{id}/activation-preview — quel équipement, et le plan
  // onSite : { identifier } ou { meterDeviceId }, et { locationId } ou { newLocation } quand la commande n'en a pas
  activationPreview(id, onSite) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/activation-preview`, method: 'get', data: onSite })
  }

  // POST /api/commissioning/orders/{id}/activation — poser et mettre en service (reprend un plan arrêté)
  activate(id, onSite) {
    return this.fetch({ url: `/api/commissioning/orders/${id}/activation`, method: 'post', data: onSite })
  }

  // GET /api/commissioning/locations-at-address — les emplacements déjà présents à une adresse
  // (params : l'adresse à plat, en crochets, préparée par l'écran)
  locationsAtAddress(organizationId, addressParams) {
    return this.fetch({ url: '/api/commissioning/locations-at-address', method: 'get', data: { organizationId, ...addressParams } })
  }

  fetch(options) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch(options)
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }
}

export default new CommissioningRepository()
